import { AreaDefinition } from '@cibo/core'
import { StaticMaps } from '@cibo/mapgl'
import { useQuery } from '@tanstack/react-query'
import bbox from '@turf/bbox'
import { feature, featureCollection } from '@turf/helpers'
import { AxiosError } from 'axios'
import { Geometry } from 'geojson'
import { logRequestError } from '../../../../consts/Theme'
import { geometryForAreaDef } from './geometryForAreaDef'
import { BoundaryThumbnailProps } from './types'

const THUMBNAIL_STYLE = {
  color: '#5aa0b1',
  width: 4,
  fill: '#5aa0b155',
}

const getThumbnailData = async (areaDef: AreaDefinition[], imgProps: any) => {
  const geometries = (await Promise.all(areaDef.map(geometryForAreaDef))).filter(
    Boolean
  ) as Geometry[]

  if (!geometries.length) return

  const fc = featureCollection(geometries.map(g => feature(g)))
  const bounds = bbox(fc)

  const rasterMap = new StaticMaps({
    width: 2 * imgProps.width,
    height: 2 * imgProps.height,
  })

  geometries.forEach(geometry => {
    switch (geometry.type) {
      case 'Polygon':
        rasterMap.addMultiPolygon({
          coords: geometry.coordinates,
          ...THUMBNAIL_STYLE,
        })
        break

      case 'MultiPolygon':
        geometry.coordinates.forEach(coords => {
          rasterMap.addMultiPolygon({
            coords,
            ...THUMBNAIL_STYLE,
          })
        })
        break
    }
  })

  const image = await rasterMap.render(bounds)

  const data = await new Promise((resolve, reject) =>
    image.getBase64('image/png', (err: Error, data: string) => (err ? reject(err) : resolve(data)))
  )

  return data
}

export const useInventoryThumbnailData = ({ boundary, ...imgProps }: BoundaryThumbnailProps) =>
  useQuery<string, AxiosError, string, any>({
    queryKey: ['inventory_boundary_thumbnail', boundary, imgProps],
    queryFn: async () => {
      if (!boundary) return
      const data = await getThumbnailData(boundary.areaDef, imgProps)
      return data
    },
    onError: (error: AxiosError) => logRequestError(error),
    enabled: !!boundary,
  })
